/* eslint-disable react/no-redundant-should-component-update */
/**
 * This file is part of the "Awaken Media" project.
 *
 * (c) 2017 - CanalPlus International
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React from 'react';
import styled from 'styled-components';
import Slider from 'react-slick';
import isEqual from 'lodash/isEqual';
import partialRight from 'lodash/partialRight';
import { AutoAffix } from 'react-overlays';

import { COLORS } from 'shared/constants/theme';
import { getRelativeWidthForTimeRange } from '../../helpers';
import { HEADER_MOBILE_HEIGHT } from 'shared/constants/heights';
import BaseChannels from '../core/Channels';
import BaseHours from '../core/Hours';
import BaseProgram from '../core/Program';
import BaseVerticalBar from '../core/VerticalBar';
import Days from '../core/Days';
import ResponsiveHoc from 'shared/components/presentational/ResponsiveHOC';
import slidersConfig from './sliders.config';

const CHANNELS_COLUMN_WIDTH = 100;

const Wrapper = styled.div`
  .TvGuide__Hours--affix {
    background-color: ${COLORS.black};
    height: 40px;
    z-index: 2;
  }
`;

const Grid = styled.div`
  z-index: 1;
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
  border-bottom: 1px solid ${COLORS.alto};
  border-right: 1px solid ${COLORS.alto};
  box-sizing: border-box;
  padding-left: ${CHANNELS_COLUMN_WIDTH}px;
`;

const HoursWrapper = styled.div`
  position: relative;
  max-width: 100%;
  margin: 0 auto;
  height: 40px;
  overflow-y: hidden;
`;

const Hours = styled(BaseHours)`
  z-index: 2;
`;

const VerticalBar = styled(BaseVerticalBar)`
  position: absolute;
  left: ${props => props.position}%;
  top: 0;
  bottom: 0;
`;

const Channels = styled(BaseChannels)`
  position: absolute;
  left: 0;
  width: ${CHANNELS_COLUMN_WIDTH}px;
`;

const Slide = styled.div`
  overflow: hidden;
  position: relative;
`;

const Program = styled(BaseProgram)`
  border-right: 1px solid ${COLORS.alto};
  height: 130px;
  overflow: hidden;
  display: inline-block;
  white-space: normal;
  vertical-align: top;
`;

const Row = styled.div`
  height: 130px;
  border-bottom: 1px solid ${COLORS.alto};
  border-right: 1px solid ${COLORS.alto};
  overflow: hidden;
  white-space: nowrap;
`;

const Empty = styled.div`
  display: inline-block;
  height: 130px;
  border-right: 1px solid ${COLORS.alto};
  width: ${props => props.size}%;
  vertical-align: top;
`;

class Complex extends React.Component {
  displayName = 'TvGuide.Complex';

  shouldComponentUpdate(nextProps) {
    return !isEqual(this.props, nextProps);
  }

  // This is a hack for slick to prevent
  // error in width calculation
  componentDidUpdate() {
    window.dispatchEvent(new Event('resize'));
  }

  // Sync Hours & Program sliders
  // Waiting for `asNavFor` to be implemented`
  // https://github.com/akiran/react-slick/issues/47
  onHourSliderChange = (oldIndex, newIndex) => {
    this.programSlider.innerSlider.slideHandler(newIndex);
  };

  onProgramSliderChange = newIndex => {
    this.hourSlider.innerSlider.slideHandler(newIndex);
  };

  render() {
    const {
      channels,
      days,
      selectedDay,
      hours,
      programs,
      fetchProgramsForChannelsIfNeeded,
      perPage,
      timezone,
      programUrlPattern,
      dayStartTimestamp,
      nowPosition,
      initialSlide,
      isToday,
      device,
      bigram,
      routerPush,
      t,
      channelsApi: { isNew },
      viewportOffsetTop
    } = this.props;

    return (
      <Wrapper className="TvGuide__Complex">
        <Days
          sliderConfig={slidersConfig.days}
          days={days}
          selectedDay={selectedDay}
          bigram={bigram}
          routerPush={routerPush}
        />
        <AutoAffix
          autoWidth
          viewportOffsetTop={viewportOffsetTop[device] || HEADER_MOBILE_HEIGHT}
          container={this}
          affixClassName="TvGuide__Hours--affix"
        >
          <HoursWrapper>
            {/* div is needed: https://github.com/react-bootstrap/react-overlays/issues/121 */}
            <Hours
              sliderRef={el => (this.hourSlider = el)}
              sliderConfig={slidersConfig.hours}
              initialSlide={initialSlide}
              hours={hours}
              onSlideChange={this.onHourSliderChange}
              t={t}
            />
          </HoursWrapper>
        </AutoAffix>
        <Grid>
          <Channels
            channels={channels}
            fetch={partialRight(
              fetchProgramsForChannelsIfNeeded,
              selectedDay,
              isNew
            )}
            perPage={perPage}
          />

          <Slider
            // this is a dirty hack to force slider to re-render
            // when initialSlide change
            key={`programSliderKey_${initialSlide}`}
            {...slidersConfig.programs}
            initialSlide={initialSlide}
            ref={el => (this.programSlider = el)}
            afterChange={this.onProgramSliderChange}
          >
            <Slide data-index={0}>
              {Object.keys(channels).map(zapNumber => {
                // If no programs for channel we add an empty row
                if (!programs[zapNumber] || programs[zapNumber].length === 0) {
                  return <Row key={`Row${zapNumber}`} />;
                }

                // Calculating empty space before first program
                const emptySpace = getRelativeWidthForTimeRange(
                  dayStartTimestamp,
                  programs[zapNumber][0][0].startTime
                );

                return (
                  <Row key={`Row${zapNumber}`}>
                    {emptySpace ? <Empty size={emptySpace} /> : false}

                    {programs[zapNumber].map((programList, programIndex) => (
                      <Program
                        key={`Channel${zapNumber}.Program.${programIndex}`}
                        programs={programList}
                        channel={channels[zapNumber]}
                        timezone={timezone}
                        urlPattern={programUrlPattern}
                        // calculating programs width
                        width={programList.reduce(
                          (acc, program) => acc + program.width,
                          0
                        )}
                        // calculating programs duration
                        duration={programList.reduce(
                          (acc, program) => acc + program.duration,
                          0
                        )}
                        isNew={isNew}
                      />
                    ))}
                  </Row>
                );
              })}
              {isToday && <VerticalBar position={nowPosition} />}
            </Slide>
          </Slider>
        </Grid>
      </Wrapper>
    );
  }
}

export default ResponsiveHoc(Complex);
